<script>
import Layout from '@/layouts/main.vue';
import PageHeader from '@/components/page-header';
import { api } from '@/helpers/common/api';
import RotuloModal from '@/components/crm/RotuloModal.vue';

export default {
  name: 'RotuloIndex',
  components: { Layout, PageHeader, RotuloModal },
  data() {
    return {
      rotulos: [],
      loading: false,
      modalAberto: false,
      rotuloEditando: null,
      filtros: { busca: '', status: '' },
      paginacao: {
        paginaAtual: 1,
        registrosPorPagina: 25,
        totalRegistros: 0,
      },
      debounceTimer: null,
    };
  },
  computed: {
    totalPaginas() {
      return Math.ceil(this.paginacao.totalRegistros / this.paginacao.registrosPorPagina) || 1;
    },
  },
  async created() {
    await this.carregarRotulos();
  },
  methods: {
    async carregarRotulos() {
      this.loading = true;
      try {
        const params = new URLSearchParams();
        if (this.filtros.busca) params.set('busca', this.filtros.busca);
        if (this.filtros.status) params.set('status', this.filtros.status);
        const offset = (this.paginacao.paginaAtual - 1) * this.paginacao.registrosPorPagina;
        params.set('offset', String(offset));
        params.set('limit', String(this.paginacao.registrosPorPagina));
        const qs = params.toString();
        const resultado = await api.read(`/rotulo${qs ? `?${qs}` : ''}`);
        if (resultado && typeof resultado === 'object') {
          this.rotulos = Array.isArray(resultado) ? resultado : resultado.data || [];
          if (resultado.total !== undefined) {
            this.paginacao.totalRegistros = resultado.total;
          }
        }
      } finally {
        this.loading = false;
      }
    },

    debounceFiltros() {
      clearTimeout(this.debounceTimer);
      this.paginacao.paginaAtual = 1;
      this.debounceTimer = setTimeout(() => {
        this.carregarRotulos();
      }, 800);
    },

    irParaPagina(pagina) {
      if (pagina >= 1 && pagina <= this.totalPaginas) {
        this.paginacao.paginaAtual = pagina;
        this.carregarRotulos();
      }
    },

    mudarRegistrosPorPagina(novoLimite) {
      this.paginacao.registrosPorPagina = Number(novoLimite);
      this.paginacao.paginaAtual = 1;
      this.carregarRotulos();
    },

    abrirModal(rotulo = null) {
      this.rotuloEditando = rotulo;
      this.modalAberto = true;
    },

    fecharModal() {
      this.modalAberto = false;
      this.rotuloEditando = null;
    },

    onRotuloSalvo() {
      this.fecharModal();
      this.carregarRotulos();
    },

    corTexto(hexCor) {
      const hex = String(hexCor || '#6c757d').replace('#', '');
      if (hex.length < 6) return '#ffffff';
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      const luminancia = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return luminancia > 0.5 ? '#212529' : '#ffffff';
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader title="Etiquetas" pageTitle="Cadastros" />

    <BRow>
      <BCol lg="12">
        <BCard no-body>
          <div class="card-header border-0 d-flex align-items-center">
            <h5 class="card-title mb-0 flex-grow-1">Lista de Etiquetas</h5>
          </div>
          <div class="card-body">
            <BRow class="g-3 mb-3 align-items-center">
              <BCol sm="4">
                <div class="search-box">
                  <input
                    v-model="filtros.busca"
                    type="text"
                    class="form-control search"
                    placeholder="Buscar por nome ou categoria..."
                    @input="debounceFiltros"
                  />
                  <i class="ri-search-line search-icon"></i>
                </div>
              </BCol>
              <BCol sm="2">
                <select v-model="filtros.status" class="form-select" @change="debounceFiltros">
                  <option value="">Todos os status</option>
                  <option value="ativo">Ativo</option>
                  <option value="inativo">Inativo</option>
                </select>
              </BCol>
              <BCol sm="2">
                <select
                  v-model.number="paginacao.registrosPorPagina"
                  class="form-select"
                  @change="mudarRegistrosPorPagina(paginacao.registrosPorPagina)"
                >
                  <option :value="25">25 por página</option>
                  <option :value="50">50 por página</option>
                  <option :value="100">100 por página</option>
                  <option :value="250">250 por página</option>
                </select>
              </BCol>
              <BCol sm="4" class="text-end">
                <BButton variant="soft-success" @click="abrirModal()">
                  <i class="ri-add-line align-bottom me-1"></i> Nova Etiqueta
                </BButton>
              </BCol>
            </BRow>

            <div v-if="loading" class="text-center py-4">
              <BSpinner variant="primary" />
            </div>
            <div v-else class="table-responsive">
              <table class="table table-borderless align-middle table-nowrap mb-0">
                <thead class="table-light">
                  <tr>
                    <th>Nome</th>
                    <th>Categoria</th>
                    <th>Descrição</th>
                    <th>Status</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="rotulo in rotulos" :key="rotulo.id">
                    <td>
                      <span
                        class="badge fs-12 fw-medium"
                        :style="{
                          backgroundColor: rotulo.cor_referencia || '#6c757d',
                          color: corTexto(rotulo.cor_referencia),
                        }"
                      >{{ rotulo.nome }}</span>
                    </td>
                    <td>{{ rotulo.categoria || '—' }}</td>
                    <td>{{ rotulo.descricao || '—' }}</td>
                    <td>
                      <span :class="rotulo.ativo ? 'badge bg-success-subtle text-success' : 'badge bg-secondary-subtle text-secondary'">
                        {{ rotulo.ativo ? 'Ativo' : 'Inativo' }}
                      </span>
                    </td>
                    <td>
                      <BButton variant="soft-info" size="sm" @click="abrirModal(rotulo)">
                        <i class="ri-edit-line"></i>
                      </BButton>
                    </td>
                  </tr>
                  <tr v-if="!rotulos.length">
                    <td colspan="5" class="text-center text-muted py-4">Nenhuma etiqueta cadastrada.</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <BRow class="mt-3 align-items-center">
              <BCol md="6" class="d-flex align-items-center">
                <small class="text-muted">
                  Mostrando
                  <strong>{{ paginacao.totalRegistros ? (paginacao.paginaAtual - 1) * paginacao.registrosPorPagina + 1 : 0 }}</strong>
                  a
                  <strong>{{ Math.min(paginacao.paginaAtual * paginacao.registrosPorPagina, paginacao.totalRegistros) }}</strong>
                  de
                  <strong>{{ paginacao.totalRegistros }}</strong>
                  registros
                </small>
              </BCol>
              <BCol md="6" class="text-end">
                <nav aria-label="Paginação">
                  <BPagination
                    v-model="paginacao.paginaAtual"
                    :total-rows="paginacao.totalRegistros"
                    :per-page="paginacao.registrosPorPagina"
                    :limit="5"
                    align="end"
                    size="sm"
                    @update:modelValue="irParaPagina"
                  />
                </nav>
              </BCol>
            </BRow>
          </div>
        </BCard>
      </BCol>
    </BRow>

    <RotuloModal
      :visivel="modalAberto"
      :rotulo="rotuloEditando"
      @fechar="fecharModal"
      @salvo="onRotuloSalvo"
    />
  </Layout>
</template>
